/**
 * LegalDocModal (#3324)
 *
 * Renders the Terms of Service / Privacy Policy documents in a dialog on top
 * of the login modal, so tapping a legal link never navigates away and drops
 * the user's in-progress sign-in (identifier + Terms checkbox live in
 * LoginModal state, which stays mounted underneath).
 *
 * Same document content as the standalone `/terms` and `/privacy` pages —
 * both render the shared `ContentDoc` data through `ContentDocView`. The doc's
 * own cross-links are intercepted (`onNavigate`): terms <-> privacy switch the
 * doc in place; anything else (contact, settings, SMS help) closes both modals
 * and routes, via the host's `onNavigateAway`.
 *
 * Layering: portals to `document.body` at the `modal` z-layer (60), above
 * LoginModal's `overlay`-equivalent z-50 (`config/zIndex.ts`). LoginModal
 * suppresses its own Escape handler while this is open, so Escape closes the
 * top-most (this) dialog only.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { TERMS_DOC } from '../data/content/terms';
import { PRIVACY_DOC } from '../data/content/privacy';
import Card from './Card';
import ContentDocView from './ContentDocView';

export type LegalDocSlug = 'terms' | 'privacy';

interface LegalDocModalProps {
  /** Which doc to open, or `null` when closed. */
  doc: LegalDocSlug | null;
  /** Close the dialog (return to the login modal in place). */
  onClose: () => void;
  /**
   * A cross-link inside the doc that is NOT terms/privacy (contact, settings,
   * SMS help). The host closes both modals and routes — terms/privacy links
   * are handled in place here and never reach this callback.
   */
  onNavigateAway: (href: string) => void;
}

const TITLES: Record<LegalDocSlug, string> = {
  terms: 'Terms of Service',
  privacy: 'Privacy Policy',
};

export default function LegalDocModal({ doc, onClose, onNavigateAway }: LegalDocModalProps) {
  // Which doc is currently shown. `doc` (the host's request) is the base; a
  // terms<->privacy cross-link inside the dialog sets `override` to flip it in
  // place without a route change. `override` resets whenever `doc` changes
  // (the "reset state on prop change" render-time pattern — no effect, so it
  // does not trip react-hooks/set-state-in-effect), so each open starts on the
  // requested doc.
  const [override, setOverride] = useState<LegalDocSlug | null>(null);
  const [seenDoc, setSeenDoc] = useState<LegalDocSlug | null>(doc);
  if (doc !== seenDoc) {
    setSeenDoc(doc);
    setOverride(null);
  }
  const active: LegalDocSlug = override ?? doc ?? 'terms';
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Move focus into the dialog on open; restore it to the trigger on close.
  // The cleanup fires when `doc` goes null (the host closes), so the login
  // modal's Terms/Privacy button regains focus.
  useEffect(() => {
    if (!doc) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    closeButtonRef.current?.focus();
    return () => {
      previouslyFocused?.focus?.();
    };
  }, [doc]);

  // #3324 (George F7): a terms<->privacy switch remounts the scroll container
  // (key={active}), which drops focus to <body> if it was on a link inside it.
  // The open/close effect above only fires on `doc`, not on an in-place switch,
  // so re-pull focus into the dialog whenever `active` changes. No cleanup —
  // this must not restore focus to the trigger on a switch (only close does).
  useEffect(() => {
    if (!doc) return;
    closeButtonRef.current?.focus();
  }, [doc, active]);

  // Escape closes this dialog. Its own listener (LoginModal stands its Escape
  // handler down while this is open) so the top-most dialog is what closes.
  useEffect(() => {
    if (!doc) return;
    const onKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeydown);
    return () => document.removeEventListener('keydown', onKeydown);
  }, [doc, onClose]);

  if (!doc) return null;

  const handleNavigate = (href: string) => {
    // #3324 (George F8): EXACT match, not startsWith — `/terms-archive` and
    // `/privacy-policy` are NOT the terms/privacy docs and must take the
    // navigate-away branch. The docs only ever link the bare `/terms` and
    // `/privacy` (verified in terms.ts / privacy.ts; no hash or query).
    if (href === '/terms') {
      setOverride('terms');
    } else if (href === '/privacy') {
      setOverride('privacy');
    } else {
      onNavigateAway(href);
    }
  };

  // #3324 (Frank F6): self-contained focus trap. `inert` on the login modal
  // hides ITS subtree, but the routed page is a SIBLING of LoginModal in the
  // tree (App.tsx) and stays tabbable — so Tab off the dialog's last control
  // (or Shift+Tab off its first) would reach content behind both layers. Wrap
  // focus within the dialog's own focusable set (close button + the doc's
  // links) on the boundary. Between boundaries the browser's native Tab order
  // handles movement; this only intercepts first<->last.
  const handleTabKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const root = dialogRef.current;
    if (!root) return;
    const focusable = Array.from(
      root.querySelectorAll<HTMLElement>(
        'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => !el.hasAttribute('disabled') && el.tabIndex !== -1);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const activeEl = document.activeElement;
    if (e.shiftKey) {
      if (activeEl === first || !root.contains(activeEl)) {
        e.preventDefault();
        last.focus();
      }
    } else if (activeEl === last || !root.contains(activeEl)) {
      e.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div
      // #3324 (Codex F1): the z-index must be a LITERAL class — Tailwind v4
      // scans source for class strings, so a runtime-computed one (the old
      // getZIndexClass('modal')) emits NO z-index rule and the dialog paints
      // UNDER LoginModal's z-50. `z-[60]` is the `modal` layer (Z_LAYERS.modal
      // = 60 in config/zIndex.ts), written out so the rule ships. Proven by a
      // grep of the built CSS + a Playwright pointer-intercept guard.
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={TITLES[active]}
      ref={dialogRef}
      onKeyDown={handleTabKey}
    >
      {/* Backdrop — dedicated to this dialog; a click closes only this layer,
          leaving the login modal underneath untouched. */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm oro-fade-in"
        onClick={onClose}
        aria-label="Close"
      />

      <div
        className="relative z-10 w-full max-w-2xl max-h-[85vh] oro-modal-enter"
        data-testid="legal-doc-modal"
      >
        <Card className="border-oro-border shadow-card bg-oro-surface overflow-hidden flex flex-col max-h-[85vh]">
          <button
            ref={closeButtonRef}
            onClick={onClose}
            className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center text-text-muted hover:text-text-primary transition-colors rounded-lg hover:bg-oro-surface-alt"
            aria-label="Close"
            data-testid="legal-doc-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
          {/* #3324 (Codex F2): key the scroll container by the active doc so a
              terms<->privacy switch remounts it — the new doc opens at the top
              instead of inheriting the previous doc's scrollTop. */}
          <div key={active} data-testid="legal-doc-scroll" className="overflow-y-auto pr-1">
            <ContentDocView doc={active === 'terms' ? TERMS_DOC : PRIVACY_DOC} onNavigate={handleNavigate} />
          </div>
        </Card>
      </div>
    </div>,
    document.body,
  );
}
